import React from "react";
import { Image } from 'antd';
import { BrandCompany, Product } from "../../../../../models/product/types";
import { BrandSectionInnerData, HelpButton, QuestionTipContainer, OneLineContainer } from "./ProductItem.styles";
import { blueDark01, blueLink, error } from "../../../../../styles/colors";
import { BoldText, Text } from "../../../../../styles/common.styles";
import { MasterFormatBackground } from "../../../../../models/product/enums";
import { EmailLink } from "../../../../mfrPage/MFRPageCompanyHeader.styles";
import { EmailContainer, ContactUs } from "./BrandInnerData.styles";
import {
    getMFROutsideTCEmailHref,
    getProductOutsideTCEmailHref,
    getFreeListingEmailHref,
} from "../../../../../helpers/manufacturerEmailHelpers";

interface Props {
    product: Product;
    brandInfo?: BrandCompany;
    sameBrand: boolean;
    productManuallyAdded: boolean;
    mfrManuallyAdded: boolean;
    manuallyAddedProductsByBrand: Product[];
    openHelp: () => void;
}

const BrandInnerData: React.FC<Props> = ({
    product,
    brandInfo,
    sameBrand,
    productManuallyAdded,
    mfrManuallyAdded,
    openHelp,
    manuallyAddedProductsByBrand
}) => {
    const companyName = brandInfo?.company || 'This manufacturer';
    
    if (!brandInfo) {
        return <></>;
    }

    if (mfrManuallyAdded) {
        return (
              <BrandSectionInnerData>
                <BoldText weight={400} size={14} height={18} color={blueDark01}>
                    {product.company}
                </BoldText>
                <OneLineContainer height={12}>
                    <Text weight={400} size={10} height={12} color={error}>
                        MFR MANUALLY ADDED
                    </Text>
                    <QuestionTipContainer style={{marginLeft:'5'+'px', marginTop:'-6'+'px'}}>
                        <Image
                            src="src/assets/images/mfr-question.svg"
                            width={11}
                            height={12}
                            preview={false}
                        />
                        <div className="tip" style={{marginLeft:'-20'+'px', width:490+'px'}}>
                          <b>This manufacturer is not in the Transparency Catalog</b> because they haven't yet invested in EPDs or material disclosures for their products.
                          <br/><br/>
                          <ContactUs href={getMFROutsideTCEmailHref(companyName)}>Contact us</ContactUs> so we can invite them to get started.
                        </div>
                    </QuestionTipContainer>
                </OneLineContainer>
                <EmailContainer>
                    <EmailLink href={getMFROutsideTCEmailHref(companyName)}>
                        Request the MFR create EPDs and/or material disclosures
                    </EmailLink>
                </EmailContainer>
              </BrandSectionInnerData>
        );
    }

    if (productManuallyAdded) {
        const productNames = manuallyAddedProductsByBrand?.map(p => p.productName) || [];
        return (
              <BrandSectionInnerData>
                <a href={brandInfo.listingUrl} target="_blank">
                    <BoldText weight={400} size={14} height={18} color={blueLink}>
                        {product.company}
                    </BoldText>
                </a>
                <OneLineContainer height={12}>
                    <Text weight={400} size={10} height={12} color={error}>
                        PRODUCT MANUALLY ADDED
                    </Text>
                    <QuestionTipContainer style={{marginLeft:'5'+'px', marginTop:'-6'+'px'}}>
                        <Image
                            src="src/assets/images/mfr-question.svg"
                            width={11}
                            height={12}
                            preview={false}
                        />
                        <div className="tip" style={{marginLeft:'-20'+'px', maxWidth:320+'px'}}>
                          <b>This MFR is in the Transparency Catalog</b>, but the product and its EPDs and/or material disclosures are not.
                          <br/><br/>
                          <ContactUs href={getProductOutsideTCEmailHref(companyName, productNames)}>Contact us</ContactUs> to recommend they add the missing product and/or information.
                        </div>
                    </QuestionTipContainer>
                </OneLineContainer>
                <EmailContainer>
                    <EmailLink href={getProductOutsideTCEmailHref(companyName, productNames)}>
                        Request MFR add to their listing
                    </EmailLink>
                </EmailContainer>
              </BrandSectionInnerData>
        );
    }

    const getSpecAndQuoteHelp = () => {
        if (brandInfo.contactEmail && brandInfo.contactInfo) {
            return (
                <HelpButton onClick={sameBrand ? undefined : openHelp}>
                    <Text weight={400} size={10} height={12} color={blueLink}>
                        Get spec & quote help
                    </Text>
                </HelpButton>
            );
        }

        return <></>;
    }

    const getProductInfoSectionTC = () => {
        if (!brandInfo) {
            return <></>;
        }
        if (brandInfo.level === MasterFormatBackground.FREE_LISTING) {
            return (
              <BrandSectionInnerData>
                  <OneLineContainer height={12}>
                      <Text weight={400} size={10} height={12} color={blueDark01}>
                          FREE LISTING
                      </Text>
                      <QuestionTipContainer style={{marginLeft:'5px', marginTop:'-6px'}}>
                          <Image
                              src="src/assets/images/mfr-question.svg"
                              width={11}
                              height={12}
                              preview={false}
                          />
                         <div className="tip" style={{marginLeft:'-20px'}}>
                             <b>This manufacturer has invested in EPDs and/or material disclosures, but has not added their products and contact info to the Transparency Catalog.</b>
                             <br/><br/>
                             <ContactUs href={getFreeListingEmailHref(companyName)}>
                                 Contact us
                             </ContactUs> to recommend they activate their listing to add the products correlated to the correct disclosures in the product table.
                         </div>
                      </QuestionTipContainer>
                  </OneLineContainer>
                <EmailContainer>
                    <EmailLink href={getFreeListingEmailHref(companyName)}>
                        Request MFR activate their listing
                    </EmailLink>
                </EmailContainer>
              </BrandSectionInnerData>
            );
        }

        if (brandInfo.level === MasterFormatBackground.FEATURED || brandInfo.level === MasterFormatBackground.STANDARD) {
            return (
                <BrandSectionInnerData>
                    <OneLineContainer height={12}>
                        <Text weight={400} size={10} height={12} color={blueDark01}>
                            ACTIVATED LISTING
                        </Text>
                        <QuestionTipContainer style={{marginLeft:'5'+'px', marginTop:'-6'+'px'}}>
                            <Image
                                src="src/assets/images/mfr-question.svg"
                                width={11}
                                height={12}
                                preview={false}
                            />
                           <div className="tip" style={{marginLeft:'-20'+'px'}}>
                               <b>This manufacturer has invested in EPDs and/or material disclosures. </b>
                                All their products and disclosures are available in the Transparency Catalog, and products are linked to pages on their website to provide more detailed info.
                           </div>
                        </QuestionTipContainer>
                    </OneLineContainer>
                    {getSpecAndQuoteHelp()}
                </BrandSectionInnerData>
              );
        }

        return <></>;
    }

    return (
        <BrandSectionInnerData>
            <a href={brandInfo.listingUrl} target="_blank">
                <BoldText weight={400} size={14} height={18} color={blueLink}>
                    {product.company}
                </BoldText>
            </a>
            {getProductInfoSectionTC()}
        </BrandSectionInnerData>
    );
}

export default BrandInnerData;
